import { useIntl } from '@capsicum/common';
import {
	ActivityIndicator,
	Button,
	Goat,
	InfoCard,
	NetworkFeeCard,
	SmartContractAddress,
	Text,
	useRouter
} from '@capsicum/common-web';
import { useSurveyUnstakeScreen } from '@capsicum/features/nc-wallet/shared';
import { useParams } from 'next/navigation';
import styled from 'styled-components';

import { useUnstakeStakingContract } from '../../hooks';

type SurveyUnstakeProps = { surveyId: string };

//TODO: Create a generic Claimable page
export const SurveyUnstake = ({ surveyId }: SurveyUnstakeProps) => {
	const { $t } = useIntl();
	const { replace } = useRouter();
	const params = useParams();

	const tokenShortCode = (params?.partnerShortCode || '') as string;

	const {
		intlConfirm,
		intlDescription,
		intlDisclaimer,
		// TRANSLATIONS
		intlTitle,
		isError,
		// GET request(s)
		isLoading,
		partnerLogo,
		stakingContractAddress,
		tokenContractAddress
	} = useSurveyUnstakeScreen({ surveyId });

	const navigateToPartnerDetailsPage = () => {
		if (tokenShortCode) {
			replace(`/nc-wallet/partner-details/${tokenShortCode}`);
		}
	};

	const { gasFeeEstimate, isButtonDisabled, sendUnstakeTransaction } = useUnstakeStakingContract({
		amount: '1',
		navigateToPartnerDetailsPage,
		stakingContractAddress: stakingContractAddress,
		tokenContractAddress: tokenContractAddress
	});

	if (isLoading) {
		return (
			<S.ActivityIndicatorContainer>
				<ActivityIndicator />
			</S.ActivityIndicatorContainer>
		);
	}

	if (isError) {
		return <Goat image="ankward" testID="surveyUnstake-errorGoat-image" title={$t('survey.details.error')} />;
	}

	return (
		<S.Wrapper>
			<S.Title variant="title5">{intlTitle}</S.Title>
			<S.Description variant="text2">{intlDescription}</S.Description>
			<S.ComponentLine>
				<InfoCard
					icon={partnerLogo}
					// We need to hardcode to 1 for mvp
					mainRightText={`1 ${tokenShortCode}`}
					testID="surveyUnstake-confirmUnstake-amount"
					title={$t('survey.unstake.unstake')}
				/>
			</S.ComponentLine>
			<S.ComponentLine>
				<NetworkFeeCard
					feeAmount={gasFeeEstimate}
					testID="surveyUnstake-confirmUnstake-networkFee"
					variant="free"
				/>
			</S.ComponentLine>
			<S.ComponentLine>
				<SmartContractAddress
					contractAddress={stakingContractAddress || ''}
					path="token"
					testID="surveyUnstake-confirmUnstake-stakingContractAddress"
					variant="link"
				/>
			</S.ComponentLine>
			<S.Description variant="text2">{intlDisclaimer}</S.Description>
			<S.ButtonWrapper>
				<S.StyledButton
					disabled={isButtonDisabled}
					isLoading={isButtonDisabled}
					testID="surveyUnstake-confirmUnstake-button"
					text={intlConfirm}
					onClick={sendUnstakeTransaction}
				/>
			</S.ButtonWrapper>
		</S.Wrapper>
	);
};

export namespace S {
	export const ActivityIndicatorContainer = styled.div`
		display: flex;
		width: 100%;
		height: 100%;
		justify-content: center;
		padding-top: ${({ theme }) => theme.spacings.xxxlarge};
	`;

	export const Wrapper = styled.div`
		flex: 1;
		background-color: ${({ theme }) => theme.colors.backgrounds.main.screen};
		justify-content: space-between;
		padding: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const Title = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Description = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.subtle};
		margin-top: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const ComponentLine = styled.div`
		margin-top: ${({ theme }) => theme.spacings.medium};
	`;

	export const ButtonWrapper = styled.div`
		display: flex;
		justify-content: center;
	`;

	export const StyledButton = styled(Button)`
		margin-top: ${({ theme }) => theme.spacings.medium};
		${({ theme }) => theme.border.radius.circle};
		align-self: center;
	`;
}
